import React, { useState } from 'react';
import { Search, X } from 'lucide-react';

interface Actor {
  id: string;
  name: string;
  boxOfficePower: number;
  recentFilms: string[];
  profilePath?: string;
}

interface ActorSearchProps {
  onSelect: (actor: Actor) => void;
  selectedActors: Actor[];
  onRemove: (actorId: string) => void;
}

const TMDB_API_KEY = 'Bearer eyJhbGciOiJIUzI1NiJ9.eyJhdWQiOiJiMjYwZGU3MGMyZDEwMzZkNzg1ZjRkYzFlYjUyODJhYiIsInN1YiI6IjVhNTZiZWYwOTI1MTQxN2FhZjAyY2Q1YyIsInNjb3BlcyI6WyJhcGlfcmVhZCJdLCJ2ZXJzaW9uIjoxfQ.QWhAbl4sFu6wXy1lnZZZdrkeBzhCqaXiof_8o9XcdZg';
const TMDB_API_URL = 'https://api.themoviedb.org/3';

const ActorSearch: React.FC<ActorSearchProps> = ({ onSelect, selectedActors, onRemove }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Actor[]>([]);
  const [loading, setLoading] = useState(false);

  const calculateBoxOfficePower = async (person: any, credits: any) => {
    try {
      const currentYear = new Date().getFullYear();
      
      // Get detailed movie information for recent films
      const recentMovies = await Promise.all(
        credits.cast
          .filter((movie: any) => {
            const year = new Date(movie.release_date).getFullYear();
            return year >= currentYear - 5 && movie.popularity > 10;
          })
          .slice(0, 5)
          .map(async (movie: any) => {
            try {
              const response = await fetch(
                `${TMDB_API_URL}/movie/${movie.id}`,
                {
                  headers: {
                    'Authorization': TMDB_API_KEY,
                    'Content-Type': 'application/json',
                  },
                }
              );
              const data = await response.json();
              return {
                ...movie,
                revenue: data.revenue || 0,
                budget: data.budget || 1,
                voteAverage: data.vote_average || 5,
                voteCount: data.vote_count || 0
              };
            } catch (error) {
              console.error('Error fetching movie details:', error);
              return {
                ...movie,
                revenue: 0,
                budget: 1,
                voteAverage: 5,
                voteCount: 0
              };
            }
          })
      );

      // Calculate factors with safe defaults
      const popularityFactor = Math.min(2, (person.popularity || 0) / 40);
      
      // Box office performance factor (0-3)
      const boxOfficeFactor = recentMovies.reduce((acc, movie) => {
        if (movie.revenue && movie.budget) {
          const roi = movie.revenue / movie.budget;
          return acc + Math.min(0.6, roi / 10); // Cap ROI contribution at 6x budget
        }
        return acc + (movie.popularity / 200); // Fallback to popularity if no financial data
      }, 0);

      // Critical reception factor (0-1.5)
      const ratingsFactor = recentMovies.reduce((acc, movie) => {
        if (movie.voteCount > 100) {
          return acc + ((movie.voteAverage - 5) / 10);
        }
        return acc;
      }, 0);

      // Career longevity (0-1)
      const firstMovie = credits.cast
        .sort((a: any, b: any) => new Date(a.release_date).getTime() - new Date(b.release_date).getTime())[0];
      const careerLength = firstMovie ? currentYear - new Date(firstMovie.release_date).getFullYear() : 0;
      const careerFactor = Math.min(1, careerLength / 20);

      // Lead role factor (0-1)
      const leadRoleFactor = recentMovies.reduce((acc, movie) => {
        return acc + (movie.order <= 2 ? 0.2 : 0.1);
      }, 0);

      // Combine all factors with safety checks
      const totalPower = (popularityFactor || 0) + 
                        (boxOfficeFactor || 0) + 
                        (ratingsFactor || 0) + 
                        (careerFactor || 0) + 
                        (leadRoleFactor || 0);
      
      // Scale to 1-8.5 range and ensure minimum of 1
      const scaledPower = Math.max(1, Math.min(8.5, 1 + (totalPower * 7.5 / 8.5)));
      
      // Final safety check
      return Number.isNaN(scaledPower) ? 1 : Number(scaledPower.toFixed(1));
    } catch (error) {
      console.error('Error calculating box office power:', error);
      return 1; // Return minimum power value if calculation fails
    }
  };

  const handleSearch = async (value: string) => {
    setQuery(value);
    if (value.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${TMDB_API_URL}/search/person?query=${encodeURIComponent(value)}&include_adult=false`,
        {
          headers: {
            'Authorization': TMDB_API_KEY,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();
      
      const actorsWithDetails = await Promise.all(
        data.results.slice(0, 5).map(async (person: any) => {
          try {
            const creditsResponse = await fetch(
              `${TMDB_API_URL}/person/${person.id}/movie_credits`,
              {
                headers: {
                  'Authorization': TMDB_API_KEY,
                  'Content-Type': 'application/json',
                },
              }
            );
            const credits = await creditsResponse.json();
            
            const recentMovies = credits.cast
              .sort((a: any, b: any) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime())
              .slice(0, 2)
              .map((movie: any) => movie.title || 'Unknown Film')
              .filter(Boolean);

            const boxOfficePower = await calculateBoxOfficePower(person, credits);

            return {
              id: person.id.toString(),
              name: person.name,
              profilePath: person.profile_path ? `https://image.tmdb.org/t/p/w92${person.profile_path}` : undefined,
              boxOfficePower,
              recentFilms: recentMovies,
            };
          } catch (error) {
            console.error('Error processing actor details:', error);
            // Return actor with minimum power if there's an error
            return {
              id: person.id.toString(),
              name: person.name,
              profilePath: person.profile_path ? `https://image.tmdb.org/t/p/w92${person.profile_path}` : undefined,
              boxOfficePower: 1,
              recentFilms: [],
            };
          }
        })
      );

      setResults(actorsWithDetails);
    } catch (error) {
      console.error('Error fetching actors:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const debounce = (func: Function, wait: number) => {
    let timeout: NodeJS.Timeout;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  const debouncedSearch = React.useCallback(
    debounce(handleSearch, 300),
    []
  );

  const handleSelect = (actor: Actor) => {
    if (selectedActors.length < 4) {
      onSelect(actor);
      setQuery('');
      setResults([]);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            debouncedSearch(e.target.value);
          }}
          placeholder={selectedActors.length >= 4 ? "Maximum actors reached" : "Search for an actor..."}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          disabled={selectedActors.length >= 4}
        />
      </div>

      {selectedActors.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedActors.map((actor) => (
            <div
              key={actor.id}
              className="flex items-center space-x-2 bg-indigo-50 px-3 py-1 rounded-full"
            >
              {actor.profilePath && (
                <img
                  src={actor.profilePath}
                  alt={actor.name}
                  className="w-6 h-6 rounded-full"
                />
              )}
              <span className="text-sm font-medium">{actor.name}</span>
              <button
                onClick={() => onRemove(actor.id)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {loading && (
        <div className="text-center py-4 text-gray-500">
          Loading...
        </div>
      )}

      {results.length > 0 && (
        <div className="border border-gray-200 rounded-md divide-y">
          {results.map((actor) => (
            <button
              key={actor.id}
              onClick={() => handleSelect(actor)}
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors flex items-center space-x-4"
              disabled={selectedActors.some(a => a.id === actor.id)}
            >
              {actor.profilePath && (
                <img
                  src={actor.profilePath}
                  alt={actor.name}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <div className="flex-1">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{actor.name}</span>
                  <span className="text-sm text-gray-600">
                    Power: {actor.boxOfficePower}
                  </span>
                </div>
                {actor.recentFilms.length > 0 && (
                  <div className="text-sm text-gray-500 mt-1">
                    Recent: {actor.recentFilms.join(', ')}
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ActorSearch;